/* ──────────────────────────────────────────────────────────────────────────
   QUEEN — Dry Body Oil · Eraé Paris
   Front-end interactions for the luxury PDP.
   ────────────────────────────────────────────────────────────────────────── */

(function () {
  function ready(fn) {
    if (document.readyState !== 'loading') return fn();
    document.addEventListener('DOMContentLoaded', fn);
  }

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  /* ──────────────────────────────────────────────────────────────────────
     A11y utilities — focus trap, focus restore, ARIA live region
     ────────────────────────────────────────────────────────────────────── */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var _trap = null; /* {container, opener, firstFocusable, lastFocusable, handler} */

  function trapFocus(container, opener) {
    releaseFocusTrap();
    var focusables = $$(FOCUSABLE, container).filter(function (el) {
      return !el.hasAttribute('hidden') && el.offsetParent !== null;
    });
    var first = focusables[0] || container;
    var last = focusables[focusables.length - 1] || container;
    function handler(e) {
      if (e.key !== 'Tab') return;
      if (focusables.length === 0) { e.preventDefault(); return; }
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', handler);
    _trap = { container: container, opener: opener, handler: handler };
    setTimeout(function () { first.focus(); }, 0);
  }
  function releaseFocusTrap() {
    if (!_trap) return;
    _trap.container.removeEventListener('keydown', _trap.handler);
    if (_trap.opener && typeof _trap.opener.focus === 'function') _trap.opener.focus();
    _trap = null;
  }

  /* ARIA live region — singleton; announces dynamic cart/price/ATC changes to screen readers */
  function getLiveRegion() {
    var lr = document.getElementById('queen-live-region');
    if (lr) return lr;
    lr = document.createElement('div');
    lr.id = 'queen-live-region';
    lr.setAttribute('role', 'status');
    lr.setAttribute('aria-live', 'polite');
    lr.setAttribute('aria-atomic', 'true');
    lr.style.cssText = 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0';
    document.body.appendChild(lr);
    return lr;
  }
  function announce(msg) {
    var lr = getLiveRegion();
    /* Clear-and-set forces SRs to re-announce identical messages */
    lr.textContent = '';
    setTimeout(function () { lr.textContent = msg; }, 50);
  }

  /* iOS-safe body scroll lock — uses position:fixed so scroll restores correctly */
  var _scrollLockY = 0;
  function lockBodyScroll() {
    _scrollLockY = window.pageYOffset || document.documentElement.scrollTop || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + _scrollLockY + 'px';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
  }
  function unlockBodyScroll() {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    window.scrollTo(0, _scrollLockY);
  }

  /* ──────────────────────────────────────────────────────────────────────
     Cart drawer — shared helpers used by ATC + bag icon click
     ────────────────────────────────────────────────────────────────────── */
  function moneyFormat(cents) {
    if (typeof cents !== 'number') return '';
    var format = (window.Shopify && window.Shopify.money_format) || '${{amount}}';
    var formatted = (cents / 100).toFixed(2);
    return format
      .replace('{{amount}}', formatted)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100))
      .replace('{{amount_with_comma_separator}}', formatted.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100));
  }

  var _cartOpener = null;
  function openCartDrawer(opener) {
    var drawer = document.querySelector('[data-queen-drawer]');
    if (!drawer) return;
    if (opener) _cartOpener = opener;
    drawer.classList.add('open');
    drawer.setAttribute('aria-hidden', 'false');
    drawer.setAttribute('aria-modal', 'true');
    lockBodyScroll();
    trapFocus(drawer, _cartOpener);
  }
  function closeCartDrawer() {
    var drawer = document.querySelector('[data-queen-drawer]');
    if (!drawer) return;
    drawer.classList.remove('open');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.removeAttribute('aria-modal');
    unlockBodyScroll();
    releaseFocusTrap();
    _cartOpener = null;
  }

  var _lastBagCount = null;
  function updateBagCount(count) {
    document.querySelectorAll('.queen-nav-icons .bag-count').forEach(function (el) {
      el.textContent = count;
    });
    if (_lastBagCount !== null && count !== _lastBagCount) {
      announce(count === 1 ? '1 item in cart' : count + ' items in cart');
    }
    _lastBagCount = count;
  }

  /* Safe DOM builder helpers — replaces innerHTML string concat to close XSS surface
     where product titles, variant titles, or line-item properties could contain
     `<`, `"`, or `&` characters that would break HTML if interpolated as strings. */
  function el(tag, attrs, text) {
    var node = document.createElement(tag);
    if (attrs) for (var k in attrs) if (attrs.hasOwnProperty(k)) node.setAttribute(k, attrs[k]);
    if (text != null) node.textContent = text;
    return node;
  }

  function renderDrawerFromCart(cart) {
    var body = document.querySelector('[data-queen-drawer-body]');
    var foot = document.querySelector('[data-queen-drawer-foot]');
    if (!body || !foot) return;

    updateBagCount(cart.item_count);

    /* Clear safely — innerHTML='' is OK because we're writing literal empty string */
    while (body.firstChild) body.removeChild(body.firstChild);

    if (cart.item_count === 0) {
      var empty = el('div', { 'class': 'queen-drawer-empty' });
      empty.appendChild(el('h3', null, 'Your atelier is quiet.'));
      empty.appendChild(el('p', null, 'Nothing reserved yet. Begin where your skin is asking to be honored first.'));
      var ghost = el('a', { href: '/collections/all', 'class': 'queen-ghost' }, 'Browse the Maison ');
      var arrow = el('span', { style: 'font-family:var(--serif);' }, '→');
      ghost.appendChild(arrow);
      empty.appendChild(ghost);
      body.appendChild(empty);
      foot.setAttribute('hidden', '');
      return;
    }

    foot.removeAttribute('hidden');

    var ul = el('ul', { 'class': 'queen-drawer-items', 'data-queen-drawer-items': '' });
    cart.items.forEach(function (item, idx) {
      var li = el('li', {
        'class': 'queen-drawer-item',
        'data-queen-drawer-item': '',
        'data-line-key': item.key,
        'data-line-index': String(idx + 1)
      });

      var thumb = el('a', { href: item.url, 'class': 'thumb' });
      if (item.image) {
        var src = item.image.replace('.jpg', '_200x.jpg').replace('.png', '_200x.png').replace('.webp', '_200x.webp');
        thumb.appendChild(el('img', {
          src: src,
          alt: item.product_title || '',
          width: '100', height: '100',
          loading: 'lazy', decoding: 'async'
        }));
      }
      li.appendChild(thumb);

      var info = el('div', { 'class': 'info' });
      info.appendChild(el('a', { href: item.url, 'class': 'title' }, item.product_title));
      if (item.variant_title && item.variant_title !== 'Default Title') {
        info.appendChild(el('span', { 'class': 'variant' }, item.variant_title));
      }
      if (item.properties) {
        Object.keys(item.properties).forEach(function (k) {
          var v = item.properties[k];
          if (!v) return;
          if (k === 'Purchase mode') {
            info.appendChild(el('span', { 'class': 'prop prop-sub' }, '↻ ' + v));
          } else {
            info.appendChild(el('span', { 'class': 'prop' }, k + ': ' + v));
          }
        });
      }

      var row = el('div', { 'class': 'row' });
      var qtyStep = el('div', { 'class': 'qty-step', 'data-queen-drawer-qty': '' });
      qtyStep.appendChild(el('button', { type: 'button', 'data-queen-drawer-qty-down': '', 'aria-label': 'Decrease quantity' }, '−'));
      qtyStep.appendChild(el('span', { 'class': 'n' }, String(item.quantity)));
      qtyStep.appendChild(el('button', { type: 'button', 'data-queen-drawer-qty-up': '', 'aria-label': 'Increase quantity' }, '+'));
      row.appendChild(qtyStep);
      row.appendChild(el('button', { type: 'button', 'class': 'remove', 'data-queen-drawer-remove': '' }, 'Remove'));
      info.appendChild(row);
      li.appendChild(info);

      li.appendChild(el('span', { 'class': 'line-price' }, moneyFormat(item.final_line_price)));
      ul.appendChild(li);
    });
    body.appendChild(ul);

    var subtotalEl = document.querySelector('[data-queen-drawer-subtotal]');
    var totalEl = document.querySelector('[data-queen-drawer-total]');
    if (subtotalEl) subtotalEl.textContent = moneyFormat(cart.items_subtotal_price || cart.total_price);
    if (totalEl) totalEl.textContent = moneyFormat(cart.total_price);
  }

  /* AbortController: each new cart request aborts any in-flight prior request so
     stale responses never race the current state. */
  var _cartFetchController = null;
  function nextCartSignal() {
    if (_cartFetchController) { try { _cartFetchController.abort(); } catch (_) {} }
    _cartFetchController = new AbortController();
    return _cartFetchController.signal;
  }
  function isAbort(err) { return err && (err.name === 'AbortError' || err.code === 20); }

  function fetchCartAndRender(thenOpen) {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' }, signal: nextCartSignal() })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        renderDrawerFromCart(cart);
        if (thenOpen) openCartDrawer();
        return cart;
      })
      .catch(function (err) { if (!isAbort(err)) throw err; });
  }

  function updateLine(line, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity }),
      signal: nextCartSignal()
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) { renderDrawerFromCart(cart); return cart; })
      .catch(function (err) { if (!isAbort(err)) throw err; });
  }

  /* Expose for ATC handler below */
  window.QueenCart = {
    open: openCartDrawer,
    close: closeCartDrawer,
    refresh: fetchCartAndRender
  };

  /* ──────────────────────────────────────────────────────────────────────
     initSection(scope) — re-runnable per-section init.
     Called once on DOMContentLoaded with document, and again on every
     shopify:section:load with the swapped-in section element. This is
     what makes the PDP editor-safe — without it, every per-element
     listener dies when a merchant reorders a section.
     ────────────────────────────────────────────────────────────────────── */
  function initSection(scope) {
    scope = scope || document;
/* ── Bundle selector ──────────────────────────────────────────────── */
var bundleRows = $$('[data-queen-bundle]', scope);
var variantInputs = $$('[data-queen-cta-variant]', scope);
var ctaPrice = $('[data-queen-cta-price]', scope);
var ctaLabel = $('[data-queen-cta-label]', scope);
var priceDisplay = $('[data-queen-price]', scope);
var priceMeta = $('[data-queen-price-meta]', scope);
var stickyPrice = $('[data-queen-sticky-price]', scope);

/* Track which purchase mode is selected so selectBundle picks the right price.
   Initialized below from whichever subscribe-toggle button Liquid rendered as
   .active — keeps initial JS state in sync with the initial Liquid render. */
var _purchaseMode = 'one_time';

function priceForRow(row) {
  if (!row) return '';
  if (_purchaseMode === 'subscribe') {
    return row.getAttribute('data-price-subscribe') || row.getAttribute('data-price-onetime') || '';
  }
  return row.getAttribute('data-price-onetime') || '';
}

function selectBundle(row) {
  bundleRows.forEach(function (r) {
    r.classList.remove('active');
    r.setAttribute('aria-checked', 'false');
    /* Update every row's visible price label to reflect the current purchase mode */
    var amt = r.querySelector('[data-queen-bundle-price-amount]');
    if (amt) amt.textContent = priceForRow(r);
  });
  row.classList.add('active');
  row.setAttribute('aria-checked', 'true');

  var variantId = row.getAttribute('data-variant-id') || '';
  var price = priceForRow(row);
  var qty = row.getAttribute('data-qty') || '1';
  var label = row.getAttribute('data-label') || '';
  var badge = row.getAttribute('data-badge') || '';

  variantInputs.forEach(function (i) { i.value = variantId; });
  /* price arrives already-formatted from the Liquid `money` filter
     (e.g. "$59.99" or "€59,99") — do not prepend a currency symbol */
  /* Re-query ctaPrice each call: the main ATC button's innerHTML is swapped during the
     Adding…/Added ✓ states, which detaches the [data-queen-cta-price] span. Without this
     re-query, bundle clicks AFTER an ATC cycle would silently write to the detached node. */
  ctaPrice = $('[data-queen-cta-price]', scope);
  if (ctaPrice) ctaPrice.textContent = price;
  if (priceDisplay) priceDisplay.textContent = price;
  if (stickyPrice) stickyPrice.textContent = price;
  if (ctaLabel && label) ctaLabel.textContent = label;
  if (priceMeta) {
    /* Per-bottle removed — keep the price-meta minimal: badge OR free-shipping */
    priceMeta.textContent = badge ? badge : 'Free shipping over $90';
  }
  /* A11y: announce the new bundle + price + mode */
  announce(qty + ' bottle' + (qty === '1' ? '' : 's') + ', ' + price + (_purchaseMode === 'subscribe' ? ', subscription' : ''));
  applySubAvailability(row);
}

function applySubAvailability(row) {
  if (!row) return;
  var available = row.getAttribute('data-subscribe-available') !== 'false';
  /* Toggle subscribe button enabled/disabled */
  purchaseModeButtons.forEach(function (b) {
    if (b.getAttribute('data-queen-purchase-mode') === 'subscribe') {
      b.disabled = !available;
      b.classList.toggle('queen-mode-disabled', !available);
      b.setAttribute('aria-disabled', !available ? 'true' : 'false');
    }
  });
  /* Show/hide the "not available" caption */
  if (subUnavailable) {
    if (available) subUnavailable.setAttribute('hidden', '');
    else subUnavailable.removeAttribute('hidden');
  }
  /* Auto-fall back to one_time if subscribe was active but the new bundle has no plan */
  if (!available && _purchaseMode === 'subscribe') {
    setPurchaseMode('one_time');
  }
}

bundleRows.forEach(function (row) {
  row.addEventListener('click', function (e) {
    e.preventDefault();
    selectBundle(row);
  });
});

/* ── Subscribe & save toggle (Recharge-aware) ─────────────────────── */
var purchaseModeButtons = $$('[data-queen-purchase-mode]', scope);
var subMeta = $('[data-queen-sub-meta]', scope);
var subUnavailable = $('[data-queen-sub-unavailable]', scope);

/* Seed _purchaseMode from whichever toggle Liquid server-rendered as .active.
   Must run BEFORE the initial selectBundle so prices paint correctly. */
var initialModeBtn = purchaseModeButtons.filter(function (b) { return b.classList.contains('active'); })[0];
if (initialModeBtn) {
  _purchaseMode = (initialModeBtn.getAttribute('data-queen-purchase-mode') === 'subscribe') ? 'subscribe' : 'one_time';
}

/* Apply the initially-active bundle so all inputs are in sync on load */
var initial = bundleRows.filter(function (r) { return r.classList.contains('active'); })[0];
if (initial) selectBundle(initial);

function setPurchaseMode(mode) {
  /* Defensive: if trying to enable subscribe but the active bundle has no Recharge allocation, ignore */
  if (mode === 'subscribe') {
    var activeBundle = bundleRows.filter(function (r) { return r.classList.contains('active'); })[0];
    if (activeBundle && activeBundle.getAttribute('data-subscribe-available') === 'false') {
      return;
    }
  }
  _purchaseMode = (mode === 'subscribe') ? 'subscribe' : 'one_time';
  purchaseModeButtons.forEach(function (b) {
    var btnMode = b.getAttribute('data-queen-purchase-mode');
    var on = (btnMode === _purchaseMode);
    b.classList.toggle('active', on);
    if (b.hasAttribute('aria-checked')) b.setAttribute('aria-checked', on ? 'true' : 'false');
  });
  if (subMeta) {
    if (_purchaseMode === 'subscribe') subMeta.classList.add('show');
    else subMeta.classList.remove('show');
  }
  $$('[data-queen-atc-form]', scope).forEach(function (form) {
    /* Recharge: set or clear the selling_plan input value */
    var sellingPlan = form.querySelector('[data-queen-selling-plan]');
    if (sellingPlan) {
      var planId = sellingPlan.getAttribute('data-plan-id') || '';
      sellingPlan.value = (_purchaseMode === 'subscribe' && planId) ? planId : '';
    }
    /* Line item property for tracking */
    var existing = form.querySelector('input[name="properties[Purchase mode]"]');
    if (_purchaseMode === 'subscribe') {
      if (!existing) {
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[Purchase mode]';
        input.value = 'Subscribe & save';
        form.appendChild(input);
      }
    } else if (existing) {
      existing.remove();
    }
  });
  /* Re-render every bundle's visible price + the currently-active bundle's CTA prices */
  var active = bundleRows.filter(function (r) { return r.classList.contains('active'); })[0];
  if (active) selectBundle(active);
}

purchaseModeButtons.forEach(function (btn) {
  btn.addEventListener('click', function (e) {
    e.preventDefault();
    setPurchaseMode(btn.getAttribute('data-queen-purchase-mode'));
  });
});

/* ── ATC form submission via Shopify cart-add API ─────────────────── */
function flashCta(button, msg, isError) {
  var prev = button.innerHTML;
  var color = isError ? '#A23B2E' : '#1A3629';
  button.innerHTML = '<span>' + msg + '</span>';
  button.style.background = color;
  button.disabled = true;
  setTimeout(function () {
    button.innerHTML = prev;
    button.style.background = '';
    button.disabled = false;
  }, 2200);
}

$$('[data-queen-atc-form]', scope).forEach(function (form) {
  form.addEventListener('submit', function (e) {
    var button = form.querySelector('button[type="submit"]');
    var variantInput = form.querySelector('input[name="id"]');
    var variantId = variantInput && variantInput.value;
    if (!variantId) {
      e.preventDefault();
      if (button) flashCta(button, 'Wire a product first', true);
      return;
    }

    e.preventDefault();
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      var prev = button.innerHTML;
      button.innerHTML = '<span>Adding…</span>';
      /* Serialize the full form so selling_plan + properties[] get sent through */
      var formData = new FormData(form);
      if (!formData.get('quantity')) formData.append('quantity', '1');
      /* Drop empty selling_plan (Shopify rejects empty value) */
      if (formData.get('selling_plan') === '') formData.delete('selling_plan');
      var body = new URLSearchParams();
      formData.forEach(function (v, k) { body.append(k, v); });
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'application/json' },
        body: body.toString(),
        signal: nextCartSignal()
      })
        .then(function (r) {
          if (!r.ok) throw new Error('Cart add failed (' + r.status + ')');
          return r.json();
        })
        .then(function () {
          button.innerHTML = '<span>Added ✓</span>';
          announce('Added to cart');
          _cartOpener = button;
          return fetchCartAndRender(true);
        })
        .then(function () {
          setTimeout(function () {
            button.innerHTML = prev;
            button.disabled = false;
            button.removeAttribute('aria-busy');
          }, 800);
        })
        .catch(function () {
          button.innerHTML = prev;
          button.disabled = false;
          button.removeAttribute('aria-busy');
          flashCta(button, 'Unavailable', true);
          announce('Unable to add to cart. Please try again.');
        });
    }
  });
});

/* ── Ingredient accordion ─────────────────────────────────────────── */
var ingRows = $$('[data-queen-ingr-row]', scope);
ingRows.forEach(function (row) {
  var btn = row.querySelector('.q, [aria-expanded]') || row;
  row.addEventListener('click', function () {
    var isOpen = row.classList.contains('open');
    ingRows.forEach(function (r) {
      r.classList.remove('open');
      var b = r.querySelector('.q, [aria-expanded]');
      if (b && b.hasAttribute('aria-expanded')) b.setAttribute('aria-expanded', 'false');
    });
    if (!isOpen) {
      row.classList.add('open');
      if (btn.hasAttribute('aria-expanded')) btn.setAttribute('aria-expanded', 'true');
    }
  });
});

/* ── FAQ + Spec accordion (shared data-queen-faq-item handler) ───── */
var faqItems = $$('[data-queen-faq-item]', scope);
faqItems.forEach(function (item) {
  var btn = item.querySelector('.q, [aria-expanded]') || item;
  function toggle() {
    var isOpen = item.classList.contains('open');
    /* Collapse all siblings within the same list scope */
    var scope = item.parentElement;
    if (scope) {
      $$('[data-queen-faq-item]', scope).forEach(function (i) {
        i.classList.remove('open');
        var b = i.querySelector('.q, [aria-expanded]');
        if (b && b.hasAttribute('aria-expanded')) b.setAttribute('aria-expanded', 'false');
      });
    }
    if (!isOpen) {
      item.classList.add('open');
      if (btn.hasAttribute('aria-expanded')) btn.setAttribute('aria-expanded', 'true');
    }
  }
  /* Attach to the button if it exists, else the wrapper */
  (item.querySelector('.q') || item).addEventListener('click', function (e) {
    e.preventDefault();
    toggle();
  });
});

/* ── Review filter chips ──────────────────────────────────────────── */
var chips = $$('[data-queen-fchip]', scope);
var reviewTiles = $$('[data-queen-review][data-concern]', scope);
var filterCount = $('[data-queen-filter-count]', scope);
var totalReviewsEl = $('[data-queen-total-reviews]', scope);

function applyFilter(value) {
  var shown = 0;
  reviewTiles.forEach(function (tile) {
    var concern = tile.getAttribute('data-concern');
    if (value === 'All' || concern === value) {
      tile.classList.remove('hidden');
      shown++;
    } else {
      tile.classList.add('hidden');
    }
  });
  if (filterCount) {
    var total = totalReviewsEl ? totalReviewsEl.textContent.trim() : shown;
    filterCount.textContent = 'Showing ' + shown + ' of ' + total;
  }
}

chips.forEach(function (chip) {
  chip.addEventListener('click', function () {
    chips.forEach(function (c) {
      c.classList.remove('active');
      if (c.hasAttribute('aria-pressed')) c.setAttribute('aria-pressed', 'false');
    });
    chip.classList.add('active');
    if (chip.hasAttribute('aria-pressed')) chip.setAttribute('aria-pressed', 'true');
    applyFilter(chip.getAttribute('data-value'));
  });
});

/* ── Sticky ATC reveal — IntersectionObserver watching the initial CTA ─
   Better than scroll-listener: no main-thread cost; ties show/hide to
   the actual visibility of the in-flow ATC button. */
var sticky = $('[data-queen-sticky]', scope);
var stickyAnchor = $('[data-queen-sticky-anchor]', scope) || $('[data-queen-cta]', scope) || $('.queen-cta', scope);
if (sticky && stickyAnchor && 'IntersectionObserver' in window) {
  var stickyIO = new IntersectionObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) {
      /* Sticky shows when the in-flow CTA scrolls OUT of view */
      if (entries[i].isIntersecting) sticky.classList.remove('show');
      else sticky.classList.add('show');
    }
  }, { rootMargin: '0px 0px -64px 0px', threshold: 0 });
  stickyIO.observe(stickyAnchor);
} else if (sticky) {
  /* Fallback for very old browsers: original scroll behavior */
  var stickyTicking = false;
  function updateStickyFallback() {
    var scrolled = window.scrollY || window.pageYOffset;
    var maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    if (maxScroll <= 0) { stickyTicking = false; return; }
    if (scrolled / maxScroll >= 0.5) sticky.classList.add('show');
    else sticky.classList.remove('show');
    stickyTicking = false;
  }
  window.addEventListener('scroll', function () {
    if (!stickyTicking) { window.requestAnimationFrame(updateStickyFallback); stickyTicking = true; }
  }, { passive: true });
  updateStickyFallback();
}

/* ── Hero gallery: desktop crossfade thumbs + mobile scroll-snap swipe ─ */
var gallery = $('[data-queen-gallery]', scope);
var galleryTrack = $('[data-queen-gallery-track]', scope);
var thumbs = $$('[data-queen-thumb]', scope);
var dots = $$('[data-queen-dots] .dot', scope);

function isMobile() { return window.matchMedia('(max-width: 640px)').matches; }

function setActiveSlide(idx) {
  if (!gallery) return;
  $$('.gallery-slide', gallery).forEach(function (s) { s.classList.remove('active'); });
  var slide = gallery.querySelector('.gallery-slide[data-slide-idx="' + idx + '"]');
  if (slide) slide.classList.add('active');
  thumbs.forEach(function (t) {
    t.classList.toggle('active', t.getAttribute('data-slide-target') === String(idx));
  });
  dots.forEach(function (d, i) {
    var isActive = (i === parseInt(idx, 10));
    d.classList.toggle('active', isActive);
    if (d.hasAttribute('aria-selected')) d.setAttribute('aria-selected', isActive ? 'true' : 'false');
  });
}

/* Gallery dot click — wire dots (now buttons) to jump to that slide */
dots.forEach(function (dot) {
  dot.addEventListener('click', function (e) {
    e.stopPropagation();
    var target = dot.getAttribute('data-slide-target') || dot.getAttribute('data-dot-idx');
    if (target === null) return;
    setActiveSlide(target);
    if (galleryTrack && isMobile()) {
      var slide = gallery && gallery.querySelector('.gallery-slide[data-slide-idx="' + target + '"]');
      if (slide) galleryTrack.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    }
  });
});

/* Thumb click — works on desktop; ignored on mobile (thumbs are hidden) */
thumbs.forEach(function (thumb) {
  thumb.addEventListener('click', function (e) {
    e.stopPropagation();
    var target = thumb.getAttribute('data-slide-target');
    if (target !== null) {
      setActiveSlide(target);
      if (galleryTrack && isMobile()) {
        var slide = gallery.querySelector('.gallery-slide[data-slide-idx="' + target + '"]');
        if (slide) galleryTrack.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      }
      if (gallery.classList.contains('zoomed')) gallery.classList.remove('zoomed');
    }
  });
});

/* Mobile: update dots as the user scrolls the track */
if (galleryTrack) {
  var scrollDebounce = null;
  galleryTrack.addEventListener('scroll', function () {
    if (scrollDebounce) cancelAnimationFrame(scrollDebounce);
    scrollDebounce = requestAnimationFrame(function () {
      var slideWidth = galleryTrack.clientWidth;
      var idx = Math.round(galleryTrack.scrollLeft / slideWidth);
      setActiveSlide(idx);
    });
  }, { passive: true });
}

/* Desktop: tap-to-zoom (disabled on mobile where swipe is the gesture) */
if (gallery) {
  gallery.addEventListener('click', function (e) {
    if (isMobile()) return;
    if (e.target.closest('[data-queen-thumb]')) return;
    if (e.target.closest('[data-queen-gallery-arrow]')) return;
    gallery.classList.toggle('zoomed');
  });
}

/* Hero gallery arrow buttons — works on both desktop crossfade + mobile swipe */
$$('[data-queen-gallery-arrow]', scope).forEach(function (arrow) {
  arrow.addEventListener('click', function (e) {
    e.stopPropagation();
    e.preventDefault();
    if (!gallery) return;
    var dir = parseInt(arrow.getAttribute('data-queen-gallery-arrow'), 10);
    var slides = $$('.gallery-slide', gallery);
    if (slides.length === 0) return;

    if (isMobile() && galleryTrack) {
      /* Mobile: scroll the track by one slide width in the chosen direction */
      var slideWidth = galleryTrack.clientWidth;
      galleryTrack.scrollBy({ left: slideWidth * dir, behavior: 'smooth' });
    } else {
      /* Desktop: find current active slide, move to prev/next */
      var currentIdx = 0;
      slides.forEach(function (s, i) { if (s.classList.contains('active')) currentIdx = i; });
      var nextIdx = currentIdx + dir;
      if (nextIdx < 0) nextIdx = slides.length - 1;
      if (nextIdx >= slides.length) nextIdx = 0;
      setActiveSlide(nextIdx);
      if (gallery.classList.contains('zoomed')) gallery.classList.remove('zoomed');
    }
  });
});

/* ── Results slider (before/after testimonial scroller) ────────────── */
var resultsTrack = $('[data-queen-results-track]', scope);
var resultsDots = $$('[data-queen-results-dots] .dot', scope);
if (resultsTrack) {
  var resultsDebounce = null;
  function syncResultsDots(idx) {
    resultsDots.forEach(function (d, i) {
      var isActive = (i === idx);
      d.classList.toggle('active', isActive);
      if (d.hasAttribute('aria-selected')) d.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }
  resultsTrack.addEventListener('scroll', function () {
    if (resultsDebounce) cancelAnimationFrame(resultsDebounce);
    resultsDebounce = requestAnimationFrame(function () {
      var cards = $$('[data-results-card]', resultsTrack);
      if (cards.length === 0) return;
      var cardWidth = cards[0].offsetWidth + 20; /* gap */
      var idx = Math.round(resultsTrack.scrollLeft / cardWidth);
      if (idx >= cards.length) idx = cards.length - 1;
      syncResultsDots(idx);
    });
  }, { passive: true });

  $$('[data-queen-results-arrow]', scope).forEach(function (arrow) {
    arrow.addEventListener('click', function () {
      var dir = parseInt(arrow.getAttribute('data-queen-results-arrow'), 10);
      var cards = $$('[data-results-card]', resultsTrack);
      if (cards.length === 0) return;
      var cardWidth = cards[0].offsetWidth + 20;
      resultsTrack.scrollBy({ left: cardWidth * dir * 2, behavior: 'smooth' });
      /* Announce navigation for screen readers */
      var cur = Math.round(resultsTrack.scrollLeft / cardWidth) + dir;
      if (cur < 0) cur = 0;
      if (cur >= cards.length) cur = cards.length - 1;
      announce('Result ' + (cur + 1) + ' of ' + cards.length);
    });
  });

  /* Dot click — jump to that card; keyboard-accessible since dots are now <button>s */
  resultsDots.forEach(function (dot, i) {
    dot.addEventListener('click', function () {
      var cards = $$('[data-results-card]', resultsTrack);
      if (cards.length === 0) return;
      var cardWidth = cards[0].offsetWidth + 20;
      resultsTrack.scrollTo({ left: cardWidth * i, behavior: 'smooth' });
      syncResultsDots(i);
    });
  });

  /* Arrow-key navigation while focus is inside the track region */
  resultsTrack.addEventListener('keydown', function (e) {
    var cards = $$('[data-results-card]', resultsTrack);
    if (cards.length === 0) return;
    var cardWidth = cards[0].offsetWidth + 20;
    var dir = 0;
    if (e.key === 'ArrowLeft') dir = -1;
    else if (e.key === 'ArrowRight') dir = 1;
    else return;
    e.preventDefault();
    resultsTrack.scrollBy({ left: cardWidth * dir, behavior: 'smooth' });
    var cur = Math.round(resultsTrack.scrollLeft / cardWidth) + dir;
    if (cur < 0) cur = 0;
    if (cur >= cards.length) cur = cards.length - 1;
    announce('Result ' + (cur + 1) + ' of ' + cards.length);
  });
}

/* ── Founder video — pause when scrolled out of view, resume on autoplay setting ─
   Perf win on long pages: a muted autoplay video that's offscreen still uses
   decoder + GPU + bandwidth. Pausing on IntersectionObserver costs nothing. */
if ('IntersectionObserver' in window) {
  var fvVideos = $$('.queen-fv-video video', scope);
  if (fvVideos.length) {
    var fvIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var v = entry.target;
        if (entry.isIntersecting) {
          if (v.dataset.queenAutoplay === 'true' || v.hasAttribute('autoplay')) {
            v.play().catch(function () { /* user gesture required — silent */ });
          }
        } else {
          v.pause();
        }
      });
    }, { threshold: 0.1 });
    fvVideos.forEach(function (v) {
      if (v.hasAttribute('autoplay')) v.dataset.queenAutoplay = 'true';
      fvIO.observe(v);
    });
  }
}
  }

  ready(function () {
    /* ── Mobile menu open/close — focus-trap + Esc-to-close + iOS scroll lock ─ */
    var _menuOpener = null;
    function openMobileMenu(opener) {
      var menu = document.querySelector('[data-queen-mobile-menu]');
      var burger = document.querySelector('[data-queen-burger]');
      if (!menu) return;
      if (opener) _menuOpener = opener;
      menu.classList.add('open');
      menu.setAttribute('aria-modal', 'true');
      menu.setAttribute('aria-hidden', 'false');
      if (burger) {
        burger.classList.add('open');
        burger.setAttribute('aria-expanded', 'true');
      }
      lockBodyScroll();
      trapFocus(menu, _menuOpener);
    }
    function closeMobileMenu() {
      var menu = document.querySelector('[data-queen-mobile-menu]');
      var burger = document.querySelector('[data-queen-burger]');
      if (!menu) return;
      menu.classList.remove('open');
      menu.removeAttribute('aria-modal');
      menu.setAttribute('aria-hidden', 'true');
      if (burger) {
        burger.classList.remove('open');
        burger.setAttribute('aria-expanded', 'false');
      }
      unlockBodyScroll();
      releaseFocusTrap();
      _menuOpener = null;
    }
    window.QueenMenu = { open: openMobileMenu, close: closeMobileMenu };

    /* ── Global Esc handler — closes whichever overlay is open ────────── */
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;
      var drawer = document.querySelector('[data-queen-drawer].open');
      if (drawer) { e.preventDefault(); closeCartDrawer(); return; }
      var menu = document.querySelector('[data-queen-mobile-menu].open');
      if (menu) { e.preventDefault(); closeMobileMenu(); return; }
    });

    /* ── Burger menu + bag icon + cart drawer (all via event delegation) ─ */
    document.addEventListener('click', function (e) {
      /* Burger toggle */
      var burger = e.target.closest('[data-queen-burger]');
      if (burger) {
        e.preventDefault();
        var mobileMenu = document.querySelector('[data-queen-mobile-menu]');
        if (!mobileMenu) return;
        if (mobileMenu.classList.contains('open')) closeMobileMenu();
        else openMobileMenu(burger);
        return;
      }
      /* Mobile menu close button (the X inside the menu) */
      var menuCloser = e.target.closest('[data-queen-mobile-menu] [data-queen-menu-close], [data-queen-mobile-menu] .queen-drawer-close');
      if (menuCloser) {
        e.preventDefault();
        closeMobileMenu();
        return;
      }
      /* Mobile menu link closes the menu */
      var menuLink = e.target.closest('[data-queen-mobile-menu] a');
      if (menuLink) {
        closeMobileMenu();
      }

      /* Bag icon click → open drawer (instead of navigating to /cart) */
      var bag = e.target.closest('.queen-nav-icons a.bag');
      if (bag) {
        e.preventDefault();
        _cartOpener = bag;
        fetchCartAndRender(true);
        return;
      }
      /* Drawer close (X button, backdrop, or "Browse the Maison" link) */
      var closer = e.target.closest('[data-queen-drawer-close], [data-queen-drawer-close-link]');
      if (closer) {
        e.preventDefault();
        closeCartDrawer();
        return;
      }
      /* Drawer qty controls */
      var qtyUp = e.target.closest('[data-queen-drawer-qty-up]');
      var qtyDown = e.target.closest('[data-queen-drawer-qty-down]');
      if (qtyUp || qtyDown) {
        e.preventDefault();
        var item = (qtyUp || qtyDown).closest('[data-queen-drawer-item]');
        if (!item) return;
        var idx = parseInt(item.getAttribute('data-line-index'), 10);
        var nEl = item.querySelector('.qty-step .n');
        var current = nEl ? parseInt(nEl.textContent, 10) : 1;
        var next = qtyUp ? current + 1 : Math.max(0, current - 1);
        updateLine(idx, next);
        return;
      }
      /* Drawer remove */
      var rem = e.target.closest('[data-queen-drawer-remove]');
      if (rem) {
        e.preventDefault();
        var lineItem = rem.closest('[data-queen-drawer-item]');
        if (!lineItem) return;
        var lineIdx = parseInt(lineItem.getAttribute('data-line-index'), 10);
        updateLine(lineIdx, 0);
        return;
      }
    });

    /* Initial cart fetch on page load to sync the bag count from server cookies */
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { updateBagCount(cart.item_count); })
      .catch(function () { /* silent */ });

    initSection(document);
  });

  /* ── Theme editor lifecycle — re-init when a section is swapped/added ─
     `e.target` is the section's wrapping element rendered fresh by the editor.
     Per-element listeners attached in initSection are scoped to it. */
  document.addEventListener('shopify:section:load', function (e) {
    if (e && e.target) initSection(e.target);
  });
})();
